import { URLS, createJurisdictionHeaders } from '@/data';
import { authUserKeys } from '@/hooks/use-auth-user-config';
import { getUserInfo } from '@/hooks/use-auth-user-info';
import { filterAvailableProducts, filterHiddenProducts } from '@/hooks/use-available-products';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { logger } from '@/utils/logging';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

export const USER_SETTINGS_LOG_TAG = '[userSettings]: ';

export type UserSettings = z.infer<typeof UserSettingsSchema>;

export enum AcceptMultiplierChanges {
    NONE = 'NONE',
    HIGHER = 'HIGHER',
    ALL = 'ALL',
}

export enum AcceptEdgeCombos {
    UNDEFINED = 'UNDEFINED',
    ENABLED = 'ENABLED',
    DISABLED = 'DISABLED',
}

export enum OddsFormat {
    DECIMAL = 'DECIMAL',
    AMERICAN = 'AMERICAN',
}

// List of all default verticals that the BE can return.
export enum DefaultVertical {
    FANTASY = 'FANTASY',
    SPORTSBOOK = 'SPORTSBOOK',
    CASINO = 'CASINO',
    ARCADE = 'ARCADE',
    SSBK = 'SSBK',
    PREDICTION = 'PREDICTION',
    RIPS = 'TRADING_CARDS',
}

const DefaultVerticalSchema = z.enum([
    DefaultVertical.FANTASY,
    DefaultVertical.SPORTSBOOK,
    DefaultVertical.CASINO,
    DefaultVertical.ARCADE,
    DefaultVertical.SSBK,
    DefaultVertical.PREDICTION,
    DefaultVertical.RIPS,
]);

// Maps backend DefaultVertical enum values to frontend Product enum values
export const mapDefaultVerticalToProduct = (vertical: DefaultVertical): Product => {
    switch (vertical) {
        case DefaultVertical.FANTASY:
            return Product.Pickem;
        case DefaultVertical.CASINO:
            return Product.Casino;
        case DefaultVertical.ARCADE:
            return Product.Arcade;
        case DefaultVertical.SSBK:
            return Product.Sweeps;
        case DefaultVertical.PREDICTION:
            return Product.Prediction;
        case DefaultVertical.RIPS:
            return Product.Rips;
        default:
            return Product.None;
    }
};

// Maps frontend Product enum values to backend DefaultVertical enum values
export const mapProductToDefaultVertical = (product: Product): DefaultVertical | null => {
    switch (product) {
        case Product.Pickem:
            return DefaultVertical.FANTASY;
        case Product.Casino:
            return DefaultVertical.CASINO;
        case Product.Arcade:
            return DefaultVertical.ARCADE;
        case Product.Sweeps:
            return DefaultVertical.SSBK;
        case Product.Prediction:
            return DefaultVertical.PREDICTION;
        case Product.Rips:
            return DefaultVertical.RIPS;
        default:
            return null;
    }
};

export const defaultUserSettings: UserSettings = {
    accept_all_odds_changes: false,
    accept_multiplier_changes: AcceptMultiplierChanges.NONE,
    accept_edge_combos: AcceptEdgeCombos.UNDEFINED,
    odds_format: OddsFormat.DECIMAL,
    hidden_verticals: [],
    can_reset_username: false,
};

type UpdateUserSettingsPayload = {
    //To be updated with other props from settings when is needed
    accept_all_odds_changes?: boolean;
    accept_multiplier_changes?: AcceptMultiplierChanges;
    accept_edge_combos?: AcceptEdgeCombos;
    odds_format?: OddsFormat;
    default_vertical?: DefaultVertical;
    hidden_verticals?: DefaultVertical[];
};

const UserSettingsSchema = z.object({
    //To be updated with other props from settings when is needed
    accept_all_odds_changes: z.boolean(), //Pickem projection changes switch
    accept_multiplier_changes: z.enum([
        AcceptMultiplierChanges.NONE,
        AcceptMultiplierChanges.HIGHER,
        AcceptMultiplierChanges.ALL,
    ]),
    accept_edge_combos: z.enum([AcceptEdgeCombos.UNDEFINED, AcceptEdgeCombos.ENABLED, AcceptEdgeCombos.DISABLED]),
    odds_format: z.enum([OddsFormat.DECIMAL, OddsFormat.AMERICAN]),
    default_vertical: DefaultVerticalSchema.optional().catch(undefined),
    // Drop only unknown verticals instead of the whole list
    hidden_verticals: z
        .array(z.unknown())
        .optional()
        .catch([])
        .transform(values =>
            (values ?? []).filter((value): value is DefaultVertical => DefaultVerticalSchema.safeParse(value).success)
        ),
    can_reset_username: z.boolean(),
});

const UserSettingsResponseSchema = z.object({
    data: UserSettingsSchema,
});

export type UserSettingsResponse = z.infer<typeof UserSettingsResponseSchema>;

export const getAuthUserSettings = async (): Promise<UserSettingsResponse> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/user/settings`, {
        method: 'GET',
        headers: createJurisdictionHeaders(),
    });
    const data = await response.json();
    if (!response.ok) {
        logger.error(USER_SETTINGS_LOG_TAG, 'Error getting user settings', data);
        throw data;
    }
    return UserSettingsResponseSchema.parse(data);
};

export const updateUserSettings = async (payload: UpdateUserSettingsPayload) => {
    const body = JSON.stringify(payload);
    const resp = await fetch(`${URLS.CHAMELON_API_URL}/auth/user/settings`, {
        method: 'PUT',
        headers: {
            ...createJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
        body,
    });
    if (!resp.ok) {
        throw await resp.json();
    }

    return await resp.json();
};

/**
 * Applies the user's default_vertical preference from settings if it's available in the jurisdiction.
 * If the user has a preference and it's available, sets that product.
 * Otherwise, falls back to the first available non-hidden product.
 * @param userEmail - Optional email to avoid an extra API call if already available
 */
export const applyUserDefaultProduct = async (userEmail?: string): Promise<{ allProductsHidden: boolean }> => {
    try {
        const userSettings = await getAuthUserSettings();
        const jurisdictionSettings = useJurisdictionStore.getState().jurisdictionSettings;
        const allProducts = jurisdictionSettings?.products ?? [];
        const productsAvailable = allProducts.length > 0;

        if (!productsAvailable) {
            return { allProductsHidden: false };
        }

        // Only fetch email if userEmail is falsy to avoid unnecessary API calls and performance
        const email = userEmail ?? (await getUserInfo()).data.email;
        const availableProducts = filterAvailableProducts(allProducts, email);
        const nonHiddenProducts = filterHiddenProducts(availableProducts, userSettings.data.hidden_verticals);
        const setProduct = useJurisdictionStore.getState().actions.setProduct;

        const userHasDefaultVertical = userSettings.data.default_vertical;

        // Try to apply the user's preferred default vertical if it's not hidden and available
        if (userHasDefaultVertical) {
            const userDefaultVerticalNotHidden = !userSettings.data.hidden_verticals.includes(userHasDefaultVertical);

            if (userDefaultVerticalNotHidden) {
                const mappedProduct = mapDefaultVerticalToProduct(userHasDefaultVertical);

                if (mappedProduct !== Product.None && availableProducts.includes(mappedProduct)) {
                    setProduct(mappedProduct);
                    return { allProductsHidden: false };
                }
            }
        }

        // Fallback: ensure the active product is not hidden
        const currentProduct = useJurisdictionStore.getState().product;

        if (nonHiddenProducts.length > 0 && (!currentProduct || !nonHiddenProducts.includes(currentProduct))) {
            setProduct(nonHiddenProducts[0]);
        }

        return { allProductsHidden: nonHiddenProducts.length === 0 };
    } catch (error) {
        logger.warn(USER_SETTINGS_LOG_TAG, 'Failed to apply user default product, using jurisdiction default', error);
        return { allProductsHidden: false };
    }
};

export const useAuthUserSettings = () => {
    const guest = useUserStore(state => state.guest);
    const sessionExpired = useUserStore(state => state.actions.sessionHasExpired);
    return useQuery({
        queryKey: authUserKeys.settings(),
        queryFn: getAuthUserSettings,
        enabled: !sessionExpired() && !guest,
        staleTime: 1000 * 60 * 3,
        select: data => data.data,
    });
};

export const useUpdateUserSettings = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateUserSettings,
        onMutate: async newSettings => {
            await queryClient.cancelQueries({ queryKey: authUserKeys.settings() });
            const previousSettings = queryClient.getQueryData(authUserKeys.settings());

            // Optimistically update the settings
            queryClient.setQueryData(authUserKeys.settings(), (old: UserSettingsResponse | undefined) => {
                if (!old) {
                    return;
                }
                return {
                    ...old,
                    data: {
                        ...old.data,
                        ...newSettings,
                    },
                };
            });

            return { previousSettings };
        },
        onError: ({}, {}, context) => {
            if (context?.previousSettings) {
                queryClient.setQueryData(authUserKeys.settings(), context.previousSettings);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: authUserKeys.settings() });
        },
    });
};
